import * as React from 'react';
import { Check, Trash, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from 'cn';

type EditableTableColumn = {
  id: string;
  header: React.ReactNode;
};

export type EditableTableRowActionState = {
  isEditing: boolean;
  startEdit: () => void;
  finishEdit: () => void;
  cancelEdit: () => void;
  requestDelete: () => void;
};

export type EditableTableAddRowActionState = {
  isAdding: boolean;
  finishAdd: () => Promise<void>;
  cancelAdd: () => void;
};

export type EditableTableProps<T> = {
  data: T[];
  columns: EditableTableColumn[];
  getRowId: (row: T) => string;
  canEdit?: boolean;
  emptyMessage?: React.ReactNode;
  renderCreateRow?: (
    actions: EditableTableAddRowActionState
  ) => React.ReactNode;
  showCreateRow?: boolean;
  onCreateRowClose?: () => void;
  renderRow: (row: T, actions: EditableTableRowActionState) => React.ReactNode;
  onConfirmAdd?: () => Promise<void> | void;
  onConfirmDelete?: (row: T) => Promise<void>;
  deleteTitle?: React.ReactNode;
  deleteDescription?: React.ReactNode;
};

export function EditableTable<T>({
  data,
  columns,
  getRowId,
  canEdit = true,
  emptyMessage = 'No results.',
  renderCreateRow,
  showCreateRow = false,
  onCreateRowClose,
  renderRow,
  onConfirmAdd,
  onConfirmDelete,
  deleteTitle = 'Delete item?',
  deleteDescription = 'This item will be removed.',
}: EditableTableProps<T>) {
  const [editingRowId, setEditingRowId] = React.useState<string | null>(null);
  const [rowToDelete, setRowToDelete] = React.useState<T | null>(null);
  const [isDeleting, setIsDeleting] = React.useState(false);

  const createActions = (row: T): EditableTableRowActionState => {
    const rowId = getRowId(row);
    const isEditing = editingRowId === rowId;

    return {
      isEditing,
      startEdit: () => {
        if (canEdit) setEditingRowId(rowId);
      },
      finishEdit: () => setEditingRowId(null),
      cancelEdit: () => setEditingRowId(null),
      requestDelete: () => {
        if (canEdit) setRowToDelete(row);
      },
    };
  };

  const addActions: EditableTableAddRowActionState = {
    isAdding: showCreateRow,
    finishAdd: async () => {
      await onConfirmAdd?.();
      onCreateRowClose?.();
    },
    cancelAdd: () => onCreateRowClose?.(),
  };

  const canShowCreateRow =
    canEdit && showCreateRow && renderCreateRow && onConfirmAdd;

  const handleDelete = async () => {
    if (!rowToDelete || !onConfirmDelete) return;
    setIsDeleting(true);
    try {
      await onConfirmDelete(rowToDelete);
      setRowToDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((column) => (
              <TableHead key={column.id}>{column.header}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {canShowCreateRow && renderCreateRow(addActions)}
          {data.length ? (
            data.map((row) => {
              const actions = createActions(row);
              return (
                <React.Fragment key={getRowId(row)}>
                  {renderRow(row, actions)}
                </React.Fragment>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length}>{emptyMessage}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <AlertDialog
        open={rowToDelete !== null}
        onOpenChange={(open) => {
          if (!open && !isDeleting) setRowToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{deleteTitle}</AlertDialogTitle>
            <AlertDialogDescription>{deleteDescription}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={isDeleting}
              onClick={() => void handleDelete()}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function EditableTableRowActions({
  isEditing,
  canEdit,
  onRequestDelete,
  onSave,
  onCancel,
}: {
  isEditing: boolean;
  canEdit: boolean;
  onSave: () => void;
  onCancel: () => void;
  onRequestDelete?: () => void;
}) {
  if (!isEditing && !onRequestDelete) return null;

  return isEditing ? (
    <div className="tw:flex tw:gap-2">
      <Button
        type="button"
        size="icon-sm"
        variant="ghost"
        aria-label="Save changes"
        onClick={(event) => {
          event.stopPropagation();
          onSave();
        }}
      >
        <Check />
      </Button>
      <Button
        type="button"
        size="icon-sm"
        variant="ghost"
        aria-label="Cancel changes"
        onClick={(event) => {
          event.stopPropagation();
          onCancel();
        }}
      >
        <X />
      </Button>
    </div>
  ) : (
    <Button
      type="button"
      variant="destructive"
      size="icon-sm"
      aria-label="Delete item"
      disabled={!canEdit}
      onClick={(event) => {
        event.stopPropagation();
        onRequestDelete?.();
      }}
    >
      <Trash />
    </Button>
  );
}

export function editableRowClass(canEdit: boolean, isEditing: boolean) {
  return cn(canEdit && !isEditing && 'tw:cursor-pointer');
}

export function EditableTableCell({
  isEditing,
  displayValue,
  children,
}: {
  isEditing: boolean;
  displayValue: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <TableCell
      onClick={isEditing ? (event) => event.stopPropagation() : undefined}
    >
      {isEditing ? children : displayValue}
    </TableCell>
  );
}
